import deepEqual from 'fast-deep-equal'
import {useCallback, useEffect, useRef, useState} from 'react'

type Options = {
  rowClassName: string
  rowIdAttributeName: string
  scrollToRow: (rowId: number) => void
}

export type SelectionState = {
  anchorRowId: number
  anchorNodeXPath: string
  anchorAbsoluteOffset: number
  anchorOffset: number
  anchorRowOutside: boolean
  focusRowId: number
  focusNodeXPath: string
  focusAbsoluteOffset: number
  focusOffset: number
  focusRowOutside: boolean
} | null

type CurrentSelectionState = {
  anchorRowId: number | null
  anchorNodeXPath: string | null
  anchorAbsoluteOffset: number
  anchorOffset: number
  anchorRowOutside: boolean
  focusRowId: number | null
  focusNodeXPath: string | null
  focusAbsoluteOffset: number
  focusOffset: number
  focusRowOutside: boolean
} | null

type SyntheticSelectionState = {
  anchorElement: any
  anchorOffset: number | null
  focusElement: any
  focusOffset: number | null
} | null

type UpdateSelection = (
  firstRowId: number | null | undefined,
  lastRowId: number | null | undefined,
) => void

function getTextNodesIn(nodeElement: Node | null, includeWhitespaceNodes = true) {
  const textNodes: Text[] = []
  const whitespace = /^\s*$/

  function getTextNodes(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (includeWhitespaceNodes || !whitespace.test((node as Text).nodeValue)) {
        textNodes.push(node as Text)
      }
    } else {
      for (let i = 0, len = node.childNodes.length; i < len; ++i) {
        getTextNodes(node.childNodes[i])
      }
    }
  }
  if (nodeElement != null) {
    getTextNodes(nodeElement)
  }
  return textNodes
}

function isSelectionState(state: SelectionState | CurrentSelectionState): state is SelectionState {
  return (
    state?.anchorRowId != null &&
    state?.focusRowId != null &&
    state?.anchorNodeXPath != null &&
    state?.focusNodeXPath != null
  )
}

export default function useGridSelection({
  rowClassName,
  rowIdAttributeName,
  scrollToRow,
}: Options): [UpdateSelection, SelectionState] {
  const shiftRef = useRef<boolean | null>(null)
  const [selectionState, setSelectionState] = useState<SelectionState>(null)
  const [syntheticSelectionState, setSyntheticSelectionState] =
    useState<SyntheticSelectionState>(null)

  const getRowId: (node: Node) => number | null = useCallback(
    node => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (
          typeof (node as Element)?.className === 'string' &&
          (node as Element)?.className?.includes(rowClassName)
        ) {
          const id = (node as Element).getAttribute(rowIdAttributeName)
          return id != null ? Number(id) : null
        }
      }
      return node.parentNode != null ? getRowId(node.parentNode) : null
    },
    [rowClassName, rowIdAttributeName],
  )

  const getXPathTo: (node: Node) => string | null = useCallback(
    node => {
      let index = 0
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (
          typeof (node as Element)?.className === 'string' &&
          (node as Element).className.includes(rowClassName)
        ) {
          const rowId = (node as Element).getAttribute(rowIdAttributeName)
          return `//*[contains(@class, "${rowClassName}") and @${rowIdAttributeName}="${rowId}"]`
        }
      }

      const siblings = node.parentNode?.childNodes ?? []
      for (let i = 0; i < siblings.length; i++) {
        const siblingNode = siblings[i]
        if (siblingNode === node) {
          const current = node.nodeType === Node.ELEMENT_NODE ? (node as Element).tagName : 'text()'
          const xPath = node.parentNode != null ? getXPathTo(node.parentNode) : null
          return xPath != null ? `${xPath}/${current}[${index + 1}]` : null
        }
        if (siblingNode.nodeType === Node.ELEMENT_NODE && node.nodeType === Node.ELEMENT_NODE) {
          if ((siblingNode as Element).tagName === (node as Element).tagName) {
            index++
          }
        }
      }
      return null
    },
    [rowClassName, rowIdAttributeName],
  )

  const getElementByXPath = (path: string): Node | null =>
    document.evaluate(path, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null)
      .singleNodeValue

  const getCurrentSelectionState: () => CurrentSelectionState = useCallback(() => {
    const selection = document.getSelection()
    if (selection?.anchorNode != null && selection?.focusNode != null) {
      const anchorRowId = getRowId(selection.anchorNode)
      const anchorNodeXPath = getXPathTo(selection.anchorNode)
      const focusRowId = getRowId(selection.focusNode)
      const focusNodeXPath = getXPathTo(selection.focusNode)

      let anchorAbsoluteOffset = 0
      const anchorRange = document.createRange()
      const anchorRowSelector = `.${rowClassName}[${rowIdAttributeName}="${anchorRowId}"]`
      const anchorRowElement = document.querySelector(anchorRowSelector)
      if (anchorRowElement != null) {
        anchorRange.selectNode(anchorRowElement)
        anchorRange.setEnd(selection.anchorNode, selection.anchorOffset)
        anchorAbsoluteOffset = anchorRange.toString().length
      }

      let focusAbsoluteOffset = 0
      const focusRange = document.createRange()
      const focusRowSelector = `.${rowClassName}[${rowIdAttributeName}="${focusRowId}"]`
      const focusRowElement = document.querySelector(focusRowSelector)
      if (focusRowElement != null) {
        focusRange.selectNode(focusRowElement)
        focusRange.setEnd(selection.focusNode, selection.focusOffset)
        focusAbsoluteOffset = focusRange.toString().length
      }

      return {
        anchorRowId,
        anchorNodeXPath,
        anchorAbsoluteOffset,
        anchorOffset: selection.anchorOffset,
        anchorRowOutside: false,
        focusRowId,
        focusNodeXPath,
        focusAbsoluteOffset,
        focusOffset: selection.focusOffset,
        focusRowOutside: false,
      }
    }
    return null
  }, [getRowId, getXPathTo, rowClassName, rowIdAttributeName])

  const getNeedUpdateSelection: () => boolean = useCallback(() => {
    const currentSelectionState = getCurrentSelectionState()

    return !deepEqual(selectionState, currentSelectionState)
  }, [getCurrentSelectionState, selectionState])

  const onKeyUp = useCallback((event: KeyboardEvent) => {
    shiftRef.current = event.shiftKey
  }, [])

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const {key, shiftKey} = event
      shiftRef.current = shiftKey

      if (
        shiftKey &&
        (key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight')
      ) {
        setSelectionState(prevSelectionState => {
          if (prevSelectionState?.focusRowOutside && prevSelectionState.focusRowId != null) {
            scrollToRow(prevSelectionState.focusRowId)
          }
          return prevSelectionState
        })
      }
    },
    [scrollToRow],
  )

  const onSelectStart = useCallback(() => {
    if (!shiftRef.current) {
      setSelectionState(null)
    }
  }, [])

  const onSelectionChange = useCallback(() => {
    const currentSelectionState = getCurrentSelectionState()
    const selection = document.getSelection()

    const isSyntheticSelectionCollapsed =
      syntheticSelectionState?.anchorElement == null &&
      syntheticSelectionState?.anchorOffset == null

    const isEqualToSyntheticAnchor =
      selection?.anchorNode === syntheticSelectionState?.anchorElement &&
      selection?.anchorOffset === syntheticSelectionState?.anchorOffset

    const isEqualToSyntheticFocus =
      selection?.focusNode === syntheticSelectionState?.focusElement &&
      selection?.focusOffset === syntheticSelectionState?.focusOffset

    if (
      currentSelectionState == null ||
      ((isSyntheticSelectionCollapsed || isEqualToSyntheticAnchor) && isEqualToSyntheticFocus)
    ) {
      return
    }

    setSelectionState(prevSelectionState => {
      if (
        prevSelectionState != null &&
        currentSelectionState.focusRowId != null &&
        currentSelectionState.focusNodeXPath != null
      ) {
        const {focusRowOutside} = prevSelectionState
        const {focusRowId, focusNodeXPath, focusAbsoluteOffset, focusOffset} = currentSelectionState

        let state = prevSelectionState
        if (state.focusRowId !== focusRowId || state.focusOffset !== focusOffset) {
          state = {
            ...state,
            focusRowId,
            focusNodeXPath,
            focusAbsoluteOffset,
            focusOffset,
            focusRowOutside,
          }
        }

        return state
      }

      if (prevSelectionState == null && isSelectionState(currentSelectionState)) {
        return currentSelectionState
      }

      return prevSelectionState
    })
  }, [syntheticSelectionState, getCurrentSelectionState])

  const onSectionRendered: (
    firstRowId: number | null | undefined,
    lastRowId: number | null | undefined,
  ) => void = useCallback(
    (firstRowId, lastRowId) => {
      const selection = document.getSelection()

      if (!selection || !selectionState || !firstRowId || !lastRowId || !getNeedUpdateSelection()) {
        return
      }

      const {anchorRowId, focusRowId, anchorNodeXPath, focusNodeXPath, anchorOffset, focusOffset} =
        selectionState

      const startMessageId = anchorRowId < focusRowId ? anchorRowId : focusRowId
      const endMessageId = anchorRowId < focusRowId ? focusRowId : anchorRowId

      const allSelectionOutside = firstRowId > endMessageId || lastRowId < startMessageId
      const fullScreenSelection = firstRowId > startMessageId && lastRowId < endMessageId

      const anchorRowOutside = firstRowId > anchorRowId || lastRowId < anchorRowId
      const focusRowOutside = firstRowId > focusRowId || lastRowId < focusRowId

      setSelectionState({...selectionState, anchorRowOutside, focusRowOutside})

      const startScreenElementSelector = `.${rowClassName}[${rowIdAttributeName}="${firstRowId}"]`
      const startScreenRowElement = document.querySelector(startScreenElementSelector)

      const endScreenElementSelector = `.${rowClassName}[${rowIdAttributeName}="${lastRowId}"]`
      const endScreenRowElement = document.querySelector(endScreenElementSelector)

      const startScreenRowTextNodes = getTextNodesIn(startScreenRowElement)
      const startScreenNode = startScreenRowTextNodes?.[0]
      const startScreenOffset = 0

      const endScreenRowTextNodes = getTextNodesIn(endScreenRowElement)
      const endScreenNode = endScreenRowTextNodes?.[endScreenRowTextNodes.length - 1]
      const endScreenOffset = endScreenNode?.length ?? 0

      const anchorElement = getElementByXPath(anchorNodeXPath)
      const focusElement = getElementByXPath(focusNodeXPath)

      let newSelectionState = null

      if (anchorElement != null && focusElement != null) {
        // The selection is inside the screen
        newSelectionState = {anchorElement, anchorOffset, focusElement, focusOffset}
      } else if (allSelectionOutside) {
        // The selection is outside the screen
        if (startScreenNode != null && endScreenNode != null) {
          if (firstRowId > endMessageId) {
            newSelectionState = {
              anchorElement: null,
              anchorOffset: null,
              focusElement: startScreenNode,
              focusOffset: startScreenOffset,
            }
          } else {
            newSelectionState = {
              anchorElement: null,
              anchorOffset: null,
              focusElement: endScreenNode,
              focusOffset: endScreenOffset,
            }
          }
        }
      } else if (fullScreenSelection) {
        // The selection greater than the screen size
        if (startScreenNode != null && endScreenNode != null) {
          newSelectionState = {
            anchorElement: startScreenNode,
            anchorOffset: startScreenOffset,
            focusElement: endScreenNode,
            focusOffset: endScreenOffset,
          }
        }
      } else if (anchorRowOutside) {
        // Part of the selection is visible
        if (startScreenNode != null && focusElement != null && anchorRowId < focusRowId) {
          newSelectionState = {
            anchorElement: startScreenNode,
            anchorOffset: startScreenOffset,
            focusElement,
            focusOffset,
          }
        }
        if (endScreenNode != null && focusElement != null && anchorRowId >= focusRowId) {
          newSelectionState = {
            anchorElement: endScreenNode,
            anchorOffset: endScreenOffset,
            focusElement,
            focusOffset,
          }
        }
      } else if (focusRowOutside) {
        // Part of the selection is visible
        if (startScreenNode != null && anchorElement != null && anchorRowId > focusRowId) {
          newSelectionState = {
            anchorElement,
            anchorOffset,
            focusElement: startScreenNode,
            focusOffset: startScreenOffset,
          }
        }
        if (endScreenNode != null && anchorElement != null && anchorRowId <= focusRowId) {
          newSelectionState = {
            anchorElement,
            anchorOffset,
            focusElement: endScreenNode,
            focusOffset: endScreenOffset,
          }
        }
      }

      if (newSelectionState != null) {
        if (newSelectionState.anchorElement == null) {
          selection.collapse(newSelectionState.focusElement, newSelectionState.focusOffset)
        } else {
          selection.setBaseAndExtent(
            newSelectionState.anchorElement,
            newSelectionState.anchorOffset,
            newSelectionState.focusElement,
            newSelectionState.focusOffset,
          )
        }
        setSyntheticSelectionState(newSelectionState)
      }
    },
    [getNeedUpdateSelection, rowClassName, rowIdAttributeName, selectionState],
  )

  useEffect(() => {
    document.addEventListener('keyup', onKeyUp)
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('selectstart', onSelectStart)
    document.addEventListener('selectionchange', onSelectionChange)

    return () => {
      document.removeEventListener('keyup', onKeyUp)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('selectstart', onSelectStart)
      document.removeEventListener('selectionchange', onSelectionChange)
    }
  }, [onKeyDown, onKeyUp, onSelectStart, onSelectionChange])

  return [onSectionRendered, selectionState]
}
